import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

/**
 * Multi-select 1-hour time-window picker (Phase 70).
 * Chips display 1-hour ranges like "09–10 Uhr". The component still stores a
 * single string value for backward compatibility with PDFs/emails/admin views —
 * multiple selections are joined with ", " (e.g. "09–10 Uhr, 10–11 Uhr").
 *
 * Slots are admin-configurable via settings.time_windows = { primary, all, hint_text }.
 * Each configured entry is the *start* of a 1-hour window ("09:00" → "09–10 Uhr").
 */
const FALLBACK_PRIMARY = ['09:00', '10:00', '15:00', '17:00', '18:00'];
const FALLBACK_ALL = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00',
  '18:00', '19:00', '20:00',
];
const FALLBACK_HINT = 'Optionale Wunschzeiten – Mehrfachauswahl möglich. Wir bestätigen das passendste Zeitfenster in der Auftragsbestätigung.';

const slotLabel = (start) => {
  if (!start || typeof start !== 'string') return '';
  // Already a formatted range coming from legacy data? Just return it verbatim.
  if (/–|-/.test(start) && /uhr/i.test(start)) return start;
  const [hStr, mStr = '00'] = start.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h)) return start;
  const nextH = (h + 1) % 24;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}–${pad(nextH)} Uhr`;
};

const parseSelected = (value) => {
  if (!value) return new Set();
  return new Set(
    String(value)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
};

const TimeWindowPicker = ({ value = '', onChange }) => {
  const { settings } = useSettings();
  const tw = settings?.time_windows || {};
  const TIMES_PRIMARY_RAW = Array.isArray(tw.primary) && tw.primary.length ? tw.primary : FALLBACK_PRIMARY;
  const TIMES_ALL_RAW = Array.isArray(tw.all) && tw.all.length ? tw.all : FALLBACK_ALL;
  // Deduplicate by the *rendered* label so half-hour entries (10:30) collapse into
  // the matching full-hour slot (10–11 Uhr). User explicitly requested 1-hour windows.
  const dedupByLabel = (raws) => {
    const seen = new Set();
    const out = [];
    for (const r of raws) {
      const l = slotLabel(r);
      if (!l || seen.has(l)) continue;
      seen.add(l);
      out.push(r);
    }
    return out;
  };
  const TIMES_PRIMARY = dedupByLabel(TIMES_PRIMARY_RAW);
  const TIMES_ALL = dedupByLabel([...TIMES_PRIMARY_RAW, ...TIMES_ALL_RAW]);
  const hintText = (tw.hint_text || '').trim() || FALLBACK_HINT;

  const selected = parseSelected(value);
  const [expanded, setExpanded] = useState(false);

  // Auto-expand if any current selection is outside the primary list.
  const primaryLabels = new Set(TIMES_PRIMARY.map(slotLabel));
  const hasSecondarySelection = Array.from(selected).some((s) => !primaryLabels.has(s));
  const showAll = expanded || hasSecondarySelection;
  const visible = showAll ? TIMES_ALL : TIMES_PRIMARY;

  const toggle = (label) => {
    const next = new Set(selected);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    // Keep stable ordering (by underlying HH:00) for the stored string
    const ordered = TIMES_ALL.map(slotLabel).filter((l) => next.has(l));
    onChange?.(ordered.join(', '));
  };

  const clear = () => onChange?.('');

  const renderChip = (t) => {
    const label = slotLabel(t);
    const active = selected.has(label);
    return (
      <button
        key={t}
        type="button"
        onClick={() => toggle(label)}
        aria-pressed={active}
        data-testid={`bf-tw-${t}`}
        className={`text-xs font-bold py-1.5 px-3 rounded-full border-2 transition-all ${
          active
            ? 'bg-sky-600 text-white border-sky-600 ring-2 ring-offset-1 ring-sky-300'
            : 'bg-white text-neutral-700 border-neutral-200 hover:border-sky-300 hover:bg-sky-50'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-2" data-testid="bf-time-window">
      <div className="flex flex-wrap gap-1.5 items-center">
        {visible.map(renderChip)}
        {!showAll && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs font-bold text-sky-600 hover:text-sky-800 underline-offset-2 hover:underline px-2 py-1"
            data-testid="bf-tw-more"
          >
            Weitere Zeitfenster <ChevronDown className="h-3 w-3 inline" />
          </button>
        )}
        {showAll && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-xs font-bold text-neutral-500 hover:text-neutral-700 px-2 py-1"
            data-testid="bf-tw-less"
          >
            Weniger <ChevronUp className="h-3 w-3 inline" />
          </button>
        )}
        {selected.size > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-xs font-bold text-neutral-500 hover:text-red-600 px-2 py-1 inline-flex items-center gap-1"
            data-testid="bf-tw-clear"
          >
            <X className="h-3 w-3" /> Zurücksetzen
          </button>
        )}
      </div>
      {selected.size > 0 && (
        <p className="text-[11px] text-sky-700 font-semibold" data-testid="bf-tw-summary">
          Ausgewählt: {Array.from(selected).join(', ')}
        </p>
      )}
      <p className="text-[11px] text-neutral-500">
        {hintText}
      </p>
    </div>
  );
};

export default TimeWindowPicker;
